"use client";
import { useEffect, useState } from "react";
import Slider from "rc-slider";
import { usePWA } from "@/utlis/usePWA";
import { useIsMobile } from "@/utlis/useIsMobile";
import { useT } from "@/context/I18nContext";

export default function ShopFilter({ onFilterChange }) {
    const isPWA = usePWA();
    const isMobile = useIsMobile();
    const isCompact = isPWA || isMobile;
    const { t } = useT();

    const [onlyDiscounted, setOnlyDiscounted] = useState(false);
    const [price, setPrice] = useState([0, 10000]);
    const [selectedAvailability, setSelectedAvailability] = useState("all");
    const [selectedSuppliers, setSelectedSuppliers] = useState([]);
    const [selectedRanges, setSelectedRanges] = useState([]);
    const [useCustomRange, setUseCustomRange] = useState(false);

    const priceRanges = [
        { label: `${t("shop_under")} 50`, min: 0,    max: 50   },
        { label: "50 - 100",      min: 50,   max: 100  },
        { label: "100 - 200",     min: 100,  max: 200  },
        { label: "200 - 500",     min: 200,  max: 500  },
        { label: "500 - 1.000",   min: 500,  max: 1000 },
        { label: "1.000 - 1.500", min: 1000, max: 1500 },
        { label: "1.500 - 2.000", min: 1500, max: 2000 },
        { label: "2.000 - 3.000", min: 2000, max: 3000 },
    ];

    const handleSelectRange = (range) => {
        setUseCustomRange(false);
        setSelectedRanges((prev) =>
            prev.some((r) => r.label === range.label)
                ? prev.filter((r) => r.label !== range.label)
                : [...prev, range],
        );
    };

    const handleCustomRange = (checked) => {
        setUseCustomRange(checked);
        if (checked) setSelectedRanges([]);
    };

    const clearFilter = () => {
        setPrice([0, 10000]);
        setSelectedAvailability("all");
        setSelectedSuppliers([]);
        setSelectedRanges([]);
        setUseCustomRange(false);
        setOnlyDiscounted(false);
    };

    // Trimite filtrele la API de fiecare dată când se schimbă
    useEffect(() => {
        let minPrice = "";
        let maxPrice = "";

        if (useCustomRange) {
            minPrice = price[0];
            maxPrice = price[1];
        } else if (selectedRanges.length > 0) {
            minPrice = Math.min(...selectedRanges.map((r) => r.min));
            maxPrice = Math.max(...selectedRanges.map((r) => r.max));
        }

        onFilterChange?.({
            minPrice,
            maxPrice,
            availability: selectedAvailability,
            onlyDiscounted,
        });
    }, [price, selectedRanges, useCustomRange, selectedAvailability, onlyDiscounted]);

    // Inline styles - bat orice CSS extern (bootstrap .offcanvas-bottom{height:30vh}
    // si tema .canvas-filter{max-width:300-410px}) fara lupta de specificitate/!important.
    const sheetStyle = isCompact
        ? {
              height: "auto",
              maxHeight: "90vh",
              width: "100%",
              maxWidth: "none",
              borderRadius: "20px 20px 0 0",
              display: "flex",
              flexDirection: "column",
          }
        : undefined;

    const wrapperStyle = isCompact
        ? { height: "100%", maxHeight: "100%", minHeight: 0, display: "flex", flexDirection: "column" }
        : undefined;

    const bodyStyle = isCompact
        ? {
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              paddingBottom: "calc(20px + env(safe-area-inset-bottom))",
          }
        : undefined;

    return (
        <div
            className={`offcanvas ${isCompact ? "offcanvas-bottom" : "offcanvas-start"} canvas-filter`}
            id='filterShop'
            style={sheetStyle}>
            {isCompact && (
                <div
                    style={{
                        width: 40,
                        height: 4,
                        margin: "10px auto 0",
                        borderRadius: 999,
                        background: "#dcdfe4",
                        flexShrink: 0,
                    }}
                />
            )}

            <div className='canvas-wrapper' style={wrapperStyle}>
                <header
                    className='canvas-header'
                    style={
                        isCompact
                            ? { background: "#fff", borderRadius: "20px 20px 0 0" }
                            : undefined
                    }>
                    <div className='filter-icon'>
                        <span className='icon icon-filter' />
                        <span>{t("shop_filter")}</span>
                    </div>
                    <span className='icon-close icon-close-popup' data-bs-dismiss='offcanvas' aria-label='Close' />
                </header>

                <div className='canvas-body' style={bodyStyle}>
                    {/* PREȚ */}
                    <div className='widget-facet wrap-price'>
                        <div className='facet-title' data-bs-target='#price' data-bs-toggle='collapse' aria-expanded='true'>
                            <span>{t("shop_price")}</span>
                            <span className='icon icon-arrow-up' />
                        </div>
                        <div id='price' className='collapse show'>
                            <ul className='tf-filter-group mb_20'>
                                {priceRanges.map((range) => (
                                    <li
                                        key={range.label}
                                        className='list-item d-flex gap-12 align-items-center'
                                        onClick={() => handleSelectRange(range)}
                                        style={{ cursor: "pointer" }}>
                                        <input
                                            type='checkbox'
                                            className='tf-check'
                                            readOnly
                                            checked={selectedRanges.some((r) => r.label === range.label)}
                                        />
                                        <label className='label' style={{ cursor: "pointer" }}>
                                            {range.label}
                                        </label>
                                    </li>
                                ))}
                            </ul>

                            <hr style={{ margin: "12px 0", borderColor: "#eee" }} />

                            <div className='widget-price filter-price'>
                                <li
                                    className='list-item d-flex gap-12 align-items-center mb_12'
                                    onClick={() => handleCustomRange(!useCustomRange)}
                                    style={{ cursor: "pointer", listStyle: "none" }}>
                                    <input type='checkbox' className='tf-check' readOnly checked={useCustomRange} />
                                    <label className='label' style={{ cursor: "pointer" }}>
                                        {t("shop_custom_range")}
                                    </label>
                                </li>
                                <Slider
                                    range
                                    min={0}
                                    max={10000}
                                    value={price}
                                    onChange={(val) => { setPrice(val); setUseCustomRange(true); setSelectedRanges([]); }}
                                    disabled={!useCustomRange}
                                />
                                <div className='box-title-price' style={{ marginTop: "10px" }}>
                                    <div className='caption-price d-flex align-items-center gap-12'>
                                        <input
                                            type='number'
                                            value={price[0]}
                                            onChange={(e) => { setPrice([Number(e.target.value), price[1]]); setUseCustomRange(true); setSelectedRanges([]); }}
                                            style={{ width: "80px", padding: "4px 8px", border: "1px solid #ddd", borderRadius: "4px", textAlign: "center" }}
                                        />
                                        <span>-</span>
                                        <input
                                            type='number'
                                            value={price[1]}
                                            onChange={(e) => { setPrice([price[0], Number(e.target.value)]); setUseCustomRange(true); setSelectedRanges([]); }}
                                            style={{ width: "80px", padding: "4px 8px", border: "1px solid #ddd", borderRadius: "4px", textAlign: "center" }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* REDUCERI */}
                    <div className='widget-facet'>
                        <div className='facet-title' data-bs-target='#discounted' data-bs-toggle='collapse' aria-expanded='true'>
                            <span>{t("shop_discounted")}</span>
                            <span className='icon icon-arrow-up' />
                        </div>
                        <div id='discounted' className='collapse show'>
                            <ul className='tf-filter-group mb_36'>
                                <li
                                    className='list-item d-flex gap-12 align-items-center'
                                    onClick={() => setOnlyDiscounted((prev) => !prev)}
                                    style={{ cursor: "pointer" }}>
                                    <input type='checkbox' className='tf-check' readOnly checked={onlyDiscounted} />
                                    <label className='label' style={{ cursor: "pointer" }}>
                                        <span style={{ color: "red", fontWeight: "600" }}>{t("shop_only_discounted")}</span>
                                    </label>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* DISPONIBILITATE */}
                    <div className='widget-facet'>
                        <div className='facet-title' data-bs-target='#availability' data-bs-toggle='collapse' aria-expanded='true'>
                            <span>{t("shop_availability")}</span>
                            <span className='icon icon-arrow-up' />
                        </div>
                        <div id='availability' className='collapse show'>
                            <ul className='tf-filter-group mb_36'>
                                {[
                                    { value: "all",          label: t("shop_availability_all")         },
                                    { value: "in_stock",     label: t("shop_availability_in_stock")       },
                                    { value: "out_of_stock", label: t("shop_availability_out_of_stock")  },
                                ].map((opt) => (
                                    <li
                                        key={opt.value}
                                        className='list-item d-flex gap-12 align-items-center'
                                        onClick={() => setSelectedAvailability(opt.value)}
                                        style={{ cursor: "pointer" }}>
                                        <input type='radio' className='tf-check' readOnly checked={selectedAvailability === opt.value} />
                                        <label className='label' style={{ cursor: "pointer" }}>{opt.label}</label>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* CLEAR */}
                    <div className='mt-5'>
                        <button type='button' className='tf-btn style-2 btn-fill rounded animate-hover-btn' onClick={clearFilter} style={{ cursor: "pointer" }}>
                            {t("shop_clear_filters")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}